<template>
  <div
    class="clipr-loader"
    :class="[
      `clipr-loader--${variant}`,
      { 'clipr-loader--error': error },
    ]"
    :role="role"
    :aria-live="ariaLive"
  >
    <div
      v-if="!error"
      class="clipr-loader-spinner"
      aria-hidden="true"
    >
      <span class="clipr-loader-ring clipr-loader-ring--outer"></span>
      <span class="clipr-loader-ring clipr-loader-ring--inner"></span>
      <span class="clipr-loader-core">
        <img
          src="/favicon.svg"
          alt=""
          class="clipr-loader-logo"
        />
      </span>
    </div>
    <span
      v-else
      class="clipr-loader-icon clipr-loader-icon--error"
      aria-hidden="true"
    >
      <i class="ti-alert-circle"></i>
    </span>

    <p v-if="title && variant !== 'inline'" class="clipr-loader-title" :class="{ 'clipr-loader-title--error': error }">
      {{ title }}
    </p>
    <p v-if="subtitle" class="clipr-loader-subtitle">{{ subtitle }}</p>
    <span v-if="title && variant === 'inline'" class="clipr-loader-inline-text">{{ title }}</span>

    <slot />
  </div>
</template>

<script>
export default {
  name: "CliprLoader",
  props: {
    title: {
      type: String,
      default: "",
    },
    subtitle: {
      type: String,
      default: "",
    },
    variant: {
      type: String,
      default: "default",
      validator: (value) => ["default", "panel", "inline"].includes(value),
    },
    error: {
      type: Boolean,
      default: false,
    },
    role: {
      type: String,
      default: "status",
    },
    ariaLive: {
      type: String,
      default: "polite",
    },
  },
};
</script>

<style scoped>
.clipr-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 10px;
}

.clipr-loader--panel {
  width: 100%;
  min-height: 148px;
  gap: 8px;
  padding: 16px;
  border-radius: var(--clipr-radius-lg, 12px);
  border: 1px solid var(--clipr-border-color, rgba(148, 163, 184, 0.18));
  background: linear-gradient(
    135deg,
    var(--clipr-bg-elevated, rgba(15, 23, 42, 0.72)) 0%,
    var(--clipr-purple-overlay-medium, rgba(168, 85, 247, 0.12)) 100%
  );
  box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.12);
}

.clipr-loader--panel.clipr-loader--compact {
  min-height: 132px;
}

.clipr-loader--inline {
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  gap: 8px;
}

.clipr-loader--error {
  gap: 14px;
}

.clipr-loader-spinner {
  position: relative;
  width: 56px;
  height: 56px;
  flex-shrink: 0;
}

.clipr-loader--inline .clipr-loader-spinner {
  width: 28px;
  height: 28px;
}

.clipr-loader-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 3px solid transparent;
}

.clipr-loader--inline .clipr-loader-ring {
  border-width: 2px;
}

.clipr-loader-ring--outer {
  border-top-color: #22d3ee;
  border-right-color: rgba(34, 211, 238, 0.2);
  animation: clipr-loader-spin 1s linear infinite;
}

.clipr-loader-ring--inner {
  inset: 8px;
  border-bottom-color: #a855f7;
  border-left-color: rgba(168, 85, 247, 0.2);
  animation: clipr-loader-spin-reverse 1.4s linear infinite;
}

.clipr-loader--inline .clipr-loader-ring--inner {
  inset: 4px;
}

.clipr-loader-core {
  position: absolute;
  inset: 14px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.88);
  border: 1px solid rgba(196, 181, 253, 0.24);
  box-shadow: 0 0 0 6px rgba(168, 85, 247, 0.08);
}

.clipr-loader--inline .clipr-loader-core {
  inset: 7px;
  box-shadow: 0 0 0 4px rgba(168, 85, 247, 0.08);
}

.clipr-loader-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.clipr-loader--inline .clipr-loader-icon {
  width: 28px;
  height: 28px;
}

.clipr-loader-icon--error {
  border: 1px solid rgba(248, 113, 113, 0.55);
  background: rgba(239, 68, 68, 0.14);
  box-shadow: 0 0 0 6px rgba(239, 68, 68, 0.08);
}

.clipr-loader-logo {
  width: 100%;
  height: 100%;
  max-width: 22px;
  max-height: 22px;
  object-fit: contain;
  display: block;
}

.clipr-loader--inline .clipr-loader-logo {
  max-width: 12px;
  max-height: 12px;
}

.clipr-loader-icon i {
  font-size: 24px;
  color: #f87171;
  line-height: 1;
}

.clipr-loader-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--clipr-text-primary, #f8fafc);
}

.clipr-loader--panel .clipr-loader-title {
  font-size: 0.9rem;
}

.clipr-loader-title--error {
  max-width: 520px;
}

.clipr-loader-subtitle {
  margin: 0;
  font-size: 0.875rem;
  color: var(--clipr-text-secondary, #94a3b8);
}

.clipr-loader--panel .clipr-loader-subtitle {
  font-size: 0.8rem;
}

.clipr-loader-inline-text {
  font-size: 0.875rem;
  color: var(--clipr-text-secondary, #94a3b8);
}

@keyframes clipr-loader-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes clipr-loader-spin-reverse {
  to {
    transform: rotate(-360deg);
  }
}
</style>
